import { useState, useContext, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { string, ref } from "yup";
import {
  Grid,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  Button,
  TextField,
  Typography,
  DialogContent,
} from "@mui/material";
import { useMutation } from "@apollo/client";

//Import from project
import "../../Style/dialogstyle.scss";
import { AuthContext } from "../../Context/AuthContext";
import { DialogTitleComp } from "../../Utils/Component";
import { CREATE_GROUP_FEE, UPDATE_GROUP_FEE } from "../../Schema/GroupFee";

export default function GroupFeeForm({
  open,
  editData,
  setRefetch,
  dialogTitle,
  handleClose,
  hideBackdrop,
}) {
  // Change Language
  const { t, setAlert } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const [createGroupFee] = useMutation(CREATE_GROUP_FEE, {
    onCompleted: ({ createGroupFee }) => {
      // console.log("createGroupFee :", createGroupFee);
      setLoading(false);
      if (createGroupFee?.isSuccess === true) {
        setAlert(true, "success", createGroupFee?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", createGroupFee?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  // console.log("createGroupFee :", createGroupFee({}));

  const [updateGroupFee] = useMutation(UPDATE_GROUP_FEE, {
    onCompleted: ({ updateGroupFee }) => {
      setLoading(false);
      if (updateGroupFee?.isSuccess === true) {
        setAlert(true, "success", updateGroupFee?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", updateGroupFee?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  //fromik
  const CheckValidation = Yup.object().shape({
    type: Yup.string().required(t("required")),
    title: Yup.string().required(t("required")),
    price: Yup.number().required(t("required")),
    remark: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      type: "Piano",
      title: "",
      price: "",
      remark: "",
    },
    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      setLoading(true);
      if (dialogTitle === "Create") {
        createGroupFee({
          variables: {
            input: {
              ...values,
            },
          },
        });
      } else {
        updateGroupFee({
          variables: {
            id: editData?._id,
            input: {
              ...values,
            },
          },
        });
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    getFieldProps,
    resetForm,
  } = formik;

  useEffect(() => {
    if (dialogTitle === "Update" && editData) {
      setFieldValue("type", editData?.type);
      setFieldValue("title", editData?.title);
      setFieldValue("price", editData?.price);
      setFieldValue("remark", editData?.remark);
    }

    if (dialogTitle === "Create" && open) {
      resetForm();
    }
  }, [open, editData]);

  return (
    <Dialog
      open={open}
      className="dialog-container"
      maxWidth="sm"
      fullWidth
      hideBackdrop={hideBackdrop}
    >
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_group_fee")
            : t("modal_title_update_group_fee")
        }
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleClose}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_title")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_title")}
                  {...getFieldProps("title")}
                  error={Boolean(touched.title && errors.title)}
                  helperText={touched.title && errors.title}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_type")}
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={values?.type}
                    onChange={({ target }) =>
                      setFieldValue("type", target?.value)
                    }
                  >
                    <MenuItem value={"Piano"}>Piano</MenuItem>
                    <MenuItem value={"Guitar"}>Guitar, violin</MenuItem>
                    <MenuItem value={"Drum"}>Drum, Bass</MenuItem>
                    <MenuItem value={"OnlineClass"}>Online Class</MenuItem>
                    <MenuItem value={"Book"}>Book</MenuItem>
                    <MenuItem value={"Instrument"}>Instrument</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_price")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  type="number"
                  InputProps={{
                    endAdornment: (
                      <Typography className={t("class-name-field-title")}>
                        $
                      </Typography>
                    ),
                  }}
                  placeholder={t("thead_price")}
                  {...getFieldProps("price")}
                  error={Boolean(touched.price && errors.price)}
                  helperText={touched.price && errors.price}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_remark")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  rows={2}
                  multiline
                  fullWidth
                  placeholder={t("thead_remark")}
                  {...getFieldProps("remark")}
                  error={Boolean(touched.remark && errors.remark)}
                  helperText={touched.remark && errors.remark}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                {loading ? (
                  <Button className={t("class-btn-create")} fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    onClick={handleSubmit}
                    className={t("class-btn-create")}
                  >
                    {dialogTitle === "Update" ? t("update") : t("create")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
